import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'

import { Col, Row } from 'antd'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import { MySelect, MySelectDarkDropdown } from '~/components/ui/MySelect'
import styles from './GridSelect.module.css'
import { OptionsTabs } from './OptionsTabs'

interface Props {
  value?: (string | number)[]
  options: Array<{
    name: string | null
    key: string | number | null
    type?: any
    category?: string | null
    isNew?: boolean | null
  }>
  onChange?: (newValue: (string | number)[]) => void
  placeholder?: string
  /** 最多可选个数 */
  max?: number
  disabled?: boolean
  /** 是否有搜索 */
  search?: boolean
  /** 检验是否可以选 */
  validateClickOption?: (val: string | number) => boolean
}

export default function GridSelect({
  value: _values,
  options,
  onChange,
  placeholder,
  max = 10,
  disabled = false,
  search = false,
  validateClickOption,
}: Props) {
  const values = _values || []
  const [visible, setVisible] = useState(false)
  const [searchMenuVisible, setSearchMenuVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [target, setTarget] = useState<any | null>()
  const [searchKey, setSearchKey] = useState('')
  // 是否单选
  const isSingleSelect = max === 1

  const tabs = Array.from(
    new Set(options.filter(item => !!item.category).map(item => item.category!))
  )

  // 点击外侧，关闭modal
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current || !e.target) return
      if (ref.current.contains(e.target as any)) {
        return
      }
      if (searchMenuVisible) return
      setVisible(false)
    }
    document.documentElement.addEventListener('click', handler)

    return () => document.documentElement.removeEventListener('click', handler)
  })

  useEffect(() => {
    // 滚动到 target 位置
    setSearchKey(target?.name ?? '')
    if (!target) return
    const categoryList = options.filter(
      item => !item?.category || item.category === target?.category
    )
    const rowIndex = Math.floor(
      categoryList.findIndex(
        option => !!option?.key && option?.key == target?.key
      ) / 4
    )
    const scrollTop = rowIndex * 48
    const row = document.getElementById('charaterGrid')
    // console.log('%d, %d, %d', currentIndex, rowIndex, scrollTop)
    if (!!row) {
      row.scrollTop = scrollTop
    }
  }, [target])

  const selected = values.filter(
    v => options.findIndex(it => it.key === v) > -1
  )

  const isDisabled = values.length >= max && max !== 1

  const handleClickOption = (key: string | number) => {
    if (disabled) return
    if (validateClickOption?.(key) ?? true) {
      if (isSingleSelect) {
        return onChange?.([key])
      } else {
        const set = new Set(values)
        if (set.has(key)) {
          return onChange?.(values.filter(v => v !== key))
        }
        if (isDisabled) return
        return onChange?.([...values, key])
      }
    }
  }

  return (
    <div className={classNames('flex flex-col', styles.gridSelect)} ref={ref}>
      <div
        className={classNames(
          'w-full border-2 border-neutral-300 border-solid rounded-xl py-2 px-4 flex items-center flex-wrap gap-2 cursor-pointer min-h-[48px] transition-all relative pr-14',
          {
            '!cursor-not-allowed': disabled,
          }
        )}
        onClick={() => {
          if (disabled) return
          setVisible(!visible)
        }}
      >
        {!selected.length && placeholder && (
          <div className="text-neutral-500">{placeholder}</div>
        )}
        {selected.map((key, index) => {
          if (isSingleSelect) {
            return (
              <div
                key={index}
                className={classNames(
                  'text-xs text-neutral-800 font-semibold flex items-center',
                  {
                    '!text-neutral-400': disabled,
                  }
                )}
              >
                <span>{options.find(item => item.key === key)?.name}</span>
              </div>
            )
          }
          return (
            <div
              key={index}
              className={classNames(
                'rounded-2xl py-1 px-4 text-xs text-neutral-800 font-semibold bg-neutral-300 flex items-center',
                {
                  '!text-neutral-400': disabled,
                }
              )}
            >
              <span>{options.find(item => item.key === key)?.name}</span>
              {!disabled && (
                <span
                  className="ml-1.5 w-3 h-3 leading-none cursor-pointer text-neutral-500 hover:text-primary transition-all relative after:absolute after:w-5 after:h-5 after:-top-1 after:-left-1"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange?.(values.filter(v => v !== key))
                  }}
                >
                  <MyIcon icon="close" className="text-[12px]"></MyIcon>
                </span>
              )}
            </div>
          )
        })}

        <button className="absolute right-2 top-1.5">
          <span className="w-6 h-8 flex items-center justify-center">
            <MyIcon
              icon="arrow-down-simple"
              className="text-neutral-500 text-[24px]"
            />
          </span>
        </button>
      </div>

      <CSSTransition
        in={visible}
        classNames="p-connected-overlay"
        unmountOnExit
        timeout={{ enter: 120, exit: 100 }}
      >
        <div
          className={classNames(
            'w-full border-2 border-neutral-200 border-solid rounded-xl py-4 px-4 scrollbar mt-3 absolute bg-neutral-200 z-50 bottom-0',
            styles.dropdownPanel
          )}
          style={{ transform: 'translateY(calc(100% + 12px))' }}
        >
          <div className="flex items-center mb-4 justify-between">
            <div className="text-neutral-500 text-xs">
              {placeholder}{' '}
              {!isSingleSelect && (
                <>
                  ({values.length}/{max})
                </>
              )}
            </div>
            {search && (
              <div className="relative items-center w-[167px]">
                <MySelect
                  className={styles.select}
                  $dark={true}
                  $rounded={true}
                  placeholder="搜索"
                  dropdownStyle={{
                    borderRadius: 12,
                    background: '#23262F',
                    padding: '8px 0',
                  }}
                  value={target?.name}
                  showSearch
                  filterOption={(input, option) => {
                    return (option?.value ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }}
                  dropdownRender={menu => {
                    return <MySelectDarkDropdown>{menu}</MySelectDarkDropdown>
                  }}
                  onDropdownVisibleChange={open => {
                    if (!open) {
                      setTimeout(() => {
                        setSearchMenuVisible(false)
                      }, 200)
                    } else {
                      setSearchMenuVisible(true)
                    }
                  }}
                  onSelect={(val: any) => {
                    // 选中同人，跳转到对应tab，并选中
                    const target = options.find(option => option.name === val)
                    if (!!target) {
                      const nextTabIndex = tabs.findIndex(
                        tab => tab === target?.category
                      )
                      if (nextTabIndex >= 0) {
                        setCurrentIndex(nextTabIndex)
                        setTarget(target)
                        if (!values.includes(target.key!!)) {
                          handleClickOption(target.key!!)
                        }
                      }
                    }
                  }}
                  onSearch={(val: any) => {
                    setSearchKey(val)
                  }}
                  suffixIcon={
                    searchKey.length <= 0 && (
                      <MyIcon
                        icon="search"
                        className="text-neutral-400 text-[20px]"
                      ></MyIcon>
                    )
                  }
                >
                  {options.map(option => (
                    <MySelect.Option key={option.key} value={option.name}>
                      {option.name}
                    </MySelect.Option>
                  ))}
                </MySelect>
                {searchKey.length > 0 && (
                  <div
                    id="gridSelectSearchClear"
                    className="absolute right-[11px] top-[6px]"
                    onClick={() => {
                      setTarget(null)
                    }}
                  >
                    <MyIcon
                      icon="close"
                      className="text-neutral-400 text-[20px]"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            {tabs.length > 1 && (
              <OptionsTabs
                options={tabs?.map(item => {
                  return {
                    label: item,
                  }
                })}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
              />
            )}

            <Row
              id="charaterGrid"
              gutter={12}
              className="mb-3 max-h-[400px] overflow-y-auto scrollbar"
            >
              {options
                .filter(
                  item =>
                    !item.category || item.category === tabs?.[currentIndex]
                )
                .map((item, index) => {
                  const isActive = values.includes(item.key!)
                  return (
                    <Col key={index} span={6} className="mb-2">
                      <div
                        className={classNames(
                          'relative cursor-pointer text-center rounded-full border-2 border-solid border-neutral-200 py-1.5 font-semibold bg-neutral-300',
                          {
                            '!border-neutral-500': isActive,
                            'cursor-not-allowed': isDisabled && !isActive,
                            [styles.bgRadial]: isActive,
                          }
                        )}
                        onClick={() => handleClickOption(item.key!)}
                      >
                        {item?.isNew && (
                          <img
                            className="h-[14px] absolute top-1 right-1"
                            src="/images/ai/new.png"
                            alt=""
                          />
                        )}
                        <div
                          className={classNames(
                            'text-sm whitespace-nowrap overflow-hidden text-ellipsis px-2 text-neutral-800 font-semibold'
                          )}
                        >
                          {item.name}
                        </div>
                      </div>
                    </Col>
                  )
                })}
            </Row>
            <div className="flex justify-between items-center mt-4 gap-3">
              <MyBtn
                block
                type="default"
                className="!bg-neutral-300 !border-neutral-200 !text-neutral-800"
                onClick={() => {
                  onChange?.([])
                  setVisible(false)
                }}
              >
                重置
              </MyBtn>
              <MyBtn block type="primary" onClick={() => setVisible(false)}>
                确定
              </MyBtn>
            </div>
          </div>
        </div>
      </CSSTransition>
    </div>
  )
}
